import { CheckCircle2, X, XCircle } from 'lucide-react';
import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';

type Toast = { id: number; message: string; type: 'success' | 'error' };
type ToastContextValue = { notify: (message: string, type?: Toast['type']) => void };
const ToastContext = createContext<ToastContextValue | null>(null);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const notify = useCallback((message: string, type: Toast['type'] = 'success') => {
    const id = Date.now();
    setToasts((items) => [...items, { id, message, type }]);
    window.setTimeout(() => setToasts((items) => items.filter((item) => item.id !== id)), 4200);
  }, []);
  const value = useMemo(() => ({ notify }), [notify]);
  return (
    <ToastContext.Provider value={value}>
      {children}
      <div className="fixed right-4 top-4 z-[100] grid w-[min(380px,calc(100vw-32px))] gap-2" aria-live="polite">
        {toasts.map((toast) => (
          <div key={toast.id} className="flex items-start gap-3 rounded-2xl border border-ink/10 bg-white p-4 shadow-soft">
            {toast.type === 'success' ? <CheckCircle2 className="mt-0.5 size-5 text-brand" /> : <XCircle className="mt-0.5 size-5 text-red-600" />}
            <p className="flex-1 text-sm font-semibold text-ink">{toast.message}</p>
            <button onClick={() => setToasts((items) => items.filter((item) => item.id !== toast.id))} aria-label="Закрыть"><X className="size-4" /></button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useToast() {
  const value = useContext(ToastContext);
  if (!value) throw new Error('useToast must be used inside ToastProvider');
  return value;
}
